import React, { useState } from "react";
import T from '../../components/common/T';
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useInventory } from "../../context/InventoryContext";
import { useAuth } from "../../context/AuthContext";
import DataTable from "../../components/common/DataTables.jsx";
import TableActionButtons from "../../components/common/TableActionButtons";
import PageHeader from "../../components/common/PageHeader";
import { TrashIcon } from "@heroicons/react/24/outline";

const InventoryListView = () => {
  const navigate = useNavigate();
  const { inventories, deleteInventory, warehouses } = useInventory();
  const [confirmDelete, setConfirmDelete] = useState(null);

  const { checkAccess } = useAuth();

  const handleDelete = (id) => {
    deleteInventory(id);
    toast.success("Item deleted successfully!", { position: "top-right", autoClose: 2000, theme: "colored" });
    setConfirmDelete(null);
  };

  const getWarehouseName = (id) => {
    const wh = warehouses.find((w) => String(w.id) === String(id));
    return wh ? wh.name : `Unknown (${id})`;
  };

  // 1. Define Columns for the DataTable
  const columns = [
    {
      header: "Item Details",
      accessor: "item_name", // Enables sorting by name
      sortable: true,
      render: (item) => (
        <div>
          <div className="font-semibold text-gray-900">{item.item_name || item.itemName}</div>
          <div className="text-[10px] text-gray-500 font-mono mt-0.5">SKU: {item.sku || "-"}</div>
        </div>
      )
    },
    {
      header: "Category",
      accessor: "category",
      sortable: true,
      render: (item) => (
        <span className="text-gray-700">{item.category || "General"}</span>
      )
    },
    {
      header: "Total Stock",
      accessor: "totalQuantity",
      sortable: true,
      align: "right", // Aligns numbers to the right (Procore style)
      render: (item) => (
        <div>
          <div className="font-semibold text-gray-900">
            {item.totalQuantity || 0} <span className="text-[10px] font-normal text-gray-500">{item.unit || "Units"}</span>
          </div>
          {Number(item.reorder_quantity) > 0 && (
            <div className="text-[10px] text-gray-400 mt-0.5">Reorder Qty: {item.reorder_quantity}</div>
          )}
        </div>
      )
    },
    {
      header: "Status",
      accessor: "status",
      sortable: true,
      align: "center",
      render: (item) => {
        const status = item.status;
        const isInStock = status === "In Stock" || status === "in_stock";
        const isOut = status === "Out of Stock" || status === "out_of_stock";
        const text = status === 'in_stock' ? 'In Stock' : status === 'out_of_stock' ? 'Out of Stock' : status === 'low_stock' ? 'Low Stock' : status;
        
        return (
          <span className={`px-2 py-0.5 text-[10px] font-semibold rounded ${
            isInStock ? "bg-green-100 text-green-800" :
            isOut ? "bg-red-100 text-red-800" :
            "bg-yellow-100 text-yellow-800"
          }`}>
            {text}
          </span>
        );
      }
    },
    {
      header: "Actions",
      align: "right",
      render: (item, { toggleExpand }) => (
        <TableActionButtons
          onView={() => toggleExpand(item.id)}
          viewTitle="View Stock Locations"
          onEdit={checkAccess('inventory.manage') ? () => navigate(`/admin/inventory/edit/${item.id}`) : undefined}
          onDelete={checkAccess('inventory.manage') ? () => setConfirmDelete(item) : undefined}
        />
      )
    }
  ];

  // 2. Define the Expanded Row Template
  const ExpandedLocationView = (item) => (
    <div className="pl-3 border-l-2 border-indigo-400 py-1">
      <h4 className="text-[11px] font-bold text-gray-600 mb-2 uppercase tracking-wide">
        <T>Stock Location Breakdown</T>
      </h4>
      {!item.locations || item.locations.length === 0 ? (
        <p className="text-[11px] text-gray-400 italic"><T>No stock allocated to specific bins.</T></p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
          {item.locations.map((loc, index) => (
            <div key={`${loc.warehouseId}-${loc.binId}-${index}`} className="bg-white border border-gray-200 rounded p-2 flex justify-between items-center shadow-sm">
              <div>
                <p className="text-[9px] text-gray-400 uppercase font-semibold"><T>Warehouse</T></p>
                <p className="text-[11px] font-semibold text-indigo-600">
                  {loc.warehouseName || getWarehouseName(loc.warehouseId)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[9px] text-gray-400 uppercase font-semibold">Bin: {loc.bin}</p>
                <p className="text-[11px] font-bold text-gray-800">{loc.qty} <span className="font-normal text-[9px]">{item.unit}</span></p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="relative">
      {/* 3. Pass Config to DataTable */}
      <DataTable 
        data={inventories}
        columns={columns}
        tableId="inventory"
        emptyMessage="No inventory items found."
        renderExpandedRow={ExpandedLocationView}
      />

      {/* Delete Modal remains untouched, living outside the table logic */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 backdrop-blur-sm">
          <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-sm text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <TrashIcon className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-base font-bold text-gray-900 mb-2"><T>Delete Item?</T></h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to delete <span className="font-semibold text-gray-800">"{confirmDelete.item_name || confirmDelete.itemName}"</span>?
            </p>
            <div className="flex justify-center gap-3">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-200 font-medium text-sm"><T>Cancel</T></button>
              <button onClick={() => handleDelete(confirmDelete.id)} className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium text-sm shadow-sm"><T>Delete</T></button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryListView;